import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/uiDir/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/uiDir/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Badge } from "@/components/uiDir/badge";
import { 
  Settings, 
  LogOut,  
  Loader2,
  Users,
  Plus,
  Bell,
  Menu,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { signOutUser } from "@/lib/authService";
import { toast } from "sonner";
import { SuggestedUsers } from "@/components/dashboard/SuggestedUsers";
import { ActivityFeed } from "@/components/dashboard/ActivityFeed";
import { Popover, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import { Sheet, SheetContent, SheetTrigger } from "@/components/uiDir/sheet";
import { ProfileCard } from "@/components/dashboard/ProfileCard";
import { QuickActionsCard } from "@/components/dashboard/QuickActionsCard";
import { ProgramRankCard } from "@/components/dashboard/ProgramRankCard";
import { UserProfile, ProfileVisitor, TrendingProject, SuggestedConnection } from "@/lib/models/dashboardmodels";
import { WelcomeBanner } from "@/components/dashboard/WelcomeBanner";
import { KeyInsights } from "@/components/dashboard/KeyInsights";
import { RecentVisitors } from "@/components/dashboard/RecentVisitors";
import { TrendingProjects } from "@/components/dashboard/TrendingProjects";
import { Opportunities } from "@/components/dashboard/Opportunities";



const Dashboard = () => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [recentVisitors, setRecentVisitors] = useState<ProfileVisitor[]>([]);
  const [trendingProjects, setTrendingProjects] = useState<TrendingProject[]>([]);
  const [suggestedConnections, setSuggestedConnections] = useState<SuggestedConnection[]>([]);
  const [programRank, setProgramRank] = useState({ rank: 0, total: 0 });
  const [unreadActivities, setUnreadActivities] = useState(0);
  const [openOpportunities, setOpenOpportunities] = useState< []>([]); 
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Call the new RPC function
        const { data, error } = await supabase.rpc('get_dashboard_data');

        if (error) {
          // Handle auth errors specifically, e.g., if JWT is expired
          if (error.message.includes("JWT")) {
            toast.error("Your session has expired. Please log in again.");
            navigate('/login');
          } else {
            throw error;
          }
          return;
        }

        // Set all state from the single response object
        setUserProfile(data.userProfile);
        setRecentVisitors(data.recentVisitors);
        setTrendingProjects(data.trendingProjects);
        setUnreadActivities(data.unreadActivities);
        setSuggestedConnections(data.suggestedConnections);
        setProgramRank(data.programRank);
        setOpenOpportunities(data.openOpportunities); 

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load your dashboard. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOutUser();
      toast.success('Signed out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now.getTime() - time.getTime()) / 1000);
    
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
          <p className="text-gray-600">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  if (!userProfile) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Modern Header */}
      <header
        className="bg-white/90 backdrop-blur-xl border-b sticky z-50 shadow-sm"
        style={{ top: "var(--beta-banner-height)" }}
      >
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="flex items-center">
              <h1 className="font-bold text-xl bg-clip-text text-primary">
                PortfolioHub
              </h1>
            </Link>
          </div>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-blue-600 font-medium">
                Dashboard
              </Button>
            </Link>
            <Link to="/discover/projects">
              <Button variant="ghost" size="sm">Discover</Button>
            </Link>
            <Link to="/dashboard/projects">
              <Button variant="ghost" size="sm">My Projects</Button>
            </Link>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* New Project Button - Desktop */}
            <Link to="/dashboard/projects/new" className="hidden sm:block">
              <Button
                size="sm"
                className="bg-primary text-white hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Project
              </Button>
            </Link>

            {/* New Project Button - Mobile (icon only) */}
            <Link to="/dashboard/projects/new" className="sm:hidden">
              <Button
                size="icon"
                className="bg-primary text-white hover:bg-primary/90 h-9 w-9"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </Link>

            {/* Notifications */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-9 w-9">
                  <Bell className="h-5 w-5" />
                  {unreadActivities > 0 && (
                    <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[calc(100vw-2rem)] max-w-sm sm:w-80 p-0">
                <ActivityFeed />
              </PopoverContent>
            </Popover>

            {/* Desktop: Settings & Logout */}
            <div className="hidden md:flex items-center gap-2">
              <Link to="/dashboard/settings">
                <Button variant="ghost" size="icon">
                  <Settings className="h-5 w-5" />
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleSignOut}>
                <LogOut className="h-5 w-5" />
              </Button>
            </div>

            {/* Mobile: Hamburger Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden h-9 w-9">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[320px]">
                <div className="flex flex-col gap-4 mt-8">
                  <Link 
                    to="/dashboard" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-blue-600 font-medium px-4 py-2 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/discover/projects" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    Discover Projects
                  </Link>
                  <Link 
                    to="/dashboard/projects" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    My Projects
                  </Link>
                  
                  <div className="border-t my-4"></div>
                  
                  <Link 
                    to="/dashboard/settings" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <Settings className="h-5 w-5" />
                    <span>Settings</span>
                  </Link>
                  <button 
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 rounded-md transition-colors text-left text-red-600"
                  >
                    <LogOut className="h-5 w-5" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Welcome Banner - Shows first on mobile, normal position on desktop */}
        <div className="lg:hidden mb-5">
          <WelcomeBanner name={userProfile.full_name} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6">
          {/* Left Sidebar - Profile & Quick Stats */}
          <div className="lg:col-span-3 space-y-5 sm:space-y-6">
            {/* Profile Card */}
            <ProfileCard userProfile={userProfile} getInitials={getInitials} />

            {/* Program Ranking
            {programRank.rank > 0 && (
              <ProgramRankCard
                rank={programRank.rank}
                total={programRank.total}
                program={userProfile.program}
              />
            )} */}

            {/* Quick Actions */}
            <QuickActionsCard />
          </div>

          {/* Center Content - Feed & Insights */}
          <div className="lg:col-span-6 space-y-5 sm:space-y-6">
            {/* Welcome Banner - Shows in normal position on desktop */}
            <div className="hidden lg:block">
              <WelcomeBanner name={userProfile.full_name} />
            </div>

            {/* Key Insights */}
            <KeyInsights profileViews={userProfile.profile_views} totalLikes={userProfile.total_likes} />

            {/* Who Viewed Your Profile */}
            {recentVisitors && recentVisitors.length > 0 && (
              <RecentVisitors visitors={recentVisitors} getInitials={getInitials} />
            )}

            {/* Trending Projects */}
            <TrendingProjects projects={trendingProjects} />
          </div>

         
          <div className="lg:col-span-3 space-y-5 sm:space-y-6">
 
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-gray-900">
                  <Users className="h-4 w-4 text-purple-600" />
                  Connect with Peers
                </CardTitle>
              </CardHeader>

              <CardContent className="pt-0">
                <SuggestedUsers isCompact />
              </CardContent>
            </Card>

            {/* Opportunities */}
            <Opportunities opportunities={openOpportunities} getInitials={getInitials} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;